import { getAuthorizations } from '@fs/zion-permissions'
import { useEffect, useState } from 'react'
import { isLoggedIn } from '@fs/zion-config'
import { authConfig } from '../config'
import useErrorHandlerOverlay from './useErrorHandlerOverlay'

const dashboardPermissions = [
  'RootsTechSpeakerAccessPermission',
  'RootsTechExhibitorAccessPermission',
  'RootsTechSponsorAccessPermission',
  'RootsTechHomelandAccessPermission',
  'RootsTechContentAdminAccessPermission',
  'RootsTechSuperAdminAccessPermission',
]

// 'RootsTechSpeakerAccessPermission' -> 'hasSpeakerAccess'
const permissionKey = (permission) => permission.replace('RootsTech', 'has').replace('Permission', '')

const NO_PERMISSIONS = dashboardPermissions.reduce((acc, permission) => {
  acc[permissionKey(permission)] = false
  return acc
}, {})

const { maxAttempts: MAX_ATTEMPTS, retryBaseDelay: RETRY_BASE_DELAY } = authConfig.permissionsLookup

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

// getAuthorizations is a plain axios.post, so on this path the rejection IS the raw axios error --
// the origError branch is future-proofing, not a shape this call site produces today. zion wraps
// errors that way only in getPermission (PermissionsError) and useAuthorizations
// (AuthorizationsError), neither of which is on this path.
const errorStatus = (error) => error?.response?.status ?? error?.origError?.response?.status

// A 4xx is definitive: the request was understood and refused, so an identical retry cannot change
// the answer. That includes 429 -- repeat requests inside a rate-limit window will not succeed and
// only count against the limit. A missing response means the request never landed (network error),
// which is worth another attempt.
//
// Note this endpoint reports "user has no permissions" as a 200 with an empty results array, never
// as a 4xx. So every failure reaching here is a genuine failure and must stay visible: a 403 means
// the request was refused (expired session, CSRF, gateway policy), which is the case RT-7496
// describes and precisely what we need telemetry for.
const isRetryable = (error) => {
  const status = errorStatus(error)
  return !status || status >= 500
}

// A single transient failure would otherwise leave every permission false for the life of the page,
// which renders the dashboard as "not a presenter" to a signed-in speaker. See RT-7496.
const fetchAuthorizations = async (isCancelled) => {
  let lastError
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await delay(RETRY_BASE_DELAY * attempt)
    }
    // Checked after the backoff so an unmount mid-wait stops the loop rather than letting the
    // remaining attempts fire against a component that no longer exists.
    if (isCancelled()) return []
    try {
      // eslint-disable-next-line no-await-in-loop
      return await getAuthorizations({ context: 'RootsTech', permissions: dashboardPermissions })
    } catch (e) {
      lastError = e
      if (!isRetryable(e)) break
    }
  }
  throw lastError
}

// Returns the permission booleans plus `resolved`, which stays false until the lookup settles.
// Callers need that distinction: before it settles, every permission is false for the same reason it
// would be false for a user with no access, and rendering "you are not a presenter" at a speaker
// whose lookup is merely in flight is the symptom RT-7496 reports.
export default function usePermissionsAccess() {
  const [permissions, setPermissions] = useState(() => ({ ...NO_PERMISSIONS }))
  const [resolved, setResolved] = useState(!isLoggedIn)
  const [error, setError] = useState()
  useErrorHandlerOverlay(error)

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined
    }

    let isCurrent = true
    fetchAuthorizations(() => !isCurrent)
      .then((results) => {
        const authorizedPermissions = results[0]?.authorizedPermissions || []
        const newPermissions = dashboardPermissions.reduce((acc, permission) => {
          acc[permissionKey(permission)] = authorizedPermissions.includes(permission) || false
          return acc
        }, {})
        if (!isCurrent) return
        setPermissions(newPermissions)
        setResolved(true)
      })
      .catch((e) => {
        if (!isCurrent) return
        setError(e)
        setResolved(true)
      })
    return () => {
      isCurrent = false
    }
  }, [])

  return { permissions, resolved }
}
